import { useEffect, useMemo } from 'react'
import { useMap } from '@vis.gl/react-maplibre'
import {
  getCommunityMapLayerInteractiveIds,
  parseCommunityMapLayerIdFromInteractiveLayerId,
} from '../../utils/community-map-layer-utils'

const HANDLER_RETRY_DELAYS_MS = [100, 300, 500, 1000]

function getReadyInteractiveLayerIds(map, interactiveLayerIds) {
  return interactiveLayerIds.filter((layerId) => map.getLayer(layerId))
}

export function CommunityMapLayerClickHandler({ layersList, enabled, onFeatureClick }) {
  const { current: map } = useMap()

  const openLayers = useMemo(
    () => (layersList ?? []).filter(
      (layer) => layer.isOpen && layer.fileContent?.features?.length,
    ),
    [layersList],
  )

  const interactiveLayerIds = useMemo(
    () => openLayers.flatMap(getCommunityMapLayerInteractiveIds),
    [openLayers],
  )

  useEffect(() => {
    if (!map?.on || !enabled || interactiveLayerIds.length === 0) return undefined

    const onClick = (event) => {
      const feature = event.features?.[0]
      if (!feature) return

      const communityLayerId = parseCommunityMapLayerIdFromInteractiveLayerId(feature.layer?.id)
      if (communityLayerId == null) return

      const communityLayer = openLayers.find(
        (layer) => Number(layer.id) === communityLayerId,
      )
      if (!communityLayer) return

      event.originalEvent?.stopPropagation()
      onFeatureClick?.({
        layerId: communityLayer.id,
        layerTitle: communityLayer.title || '',
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        properties: feature.properties || {},
      })
    }

    const onEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }

    const onLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    const attachedLayerIds = []

    const detachHandlers = () => {
      attachedLayerIds.forEach((layerId) => {
        map.off('click', layerId, onClick)
        map.off('mouseenter', layerId, onEnter)
        map.off('mouseleave', layerId, onLeave)
      })
      attachedLayerIds.length = 0
      map.getCanvas().style.cursor = ''
    }

    const attachHandlers = () => {
      const readyLayerIds = getReadyInteractiveLayerIds(map, interactiveLayerIds)
      readyLayerIds.forEach((layerId) => {
        if (attachedLayerIds.includes(layerId)) return
        map.on('click', layerId, onClick)
        map.on('mouseenter', layerId, onEnter)
        map.on('mouseleave', layerId, onLeave)
        attachedLayerIds.push(layerId)
      })
      return attachedLayerIds.length === interactiveLayerIds.length
    }

    detachHandlers()
    let handlersComplete = attachHandlers()
    const retryTimeouts = []

    if (!handlersComplete) {
      HANDLER_RETRY_DELAYS_MS.forEach((delay) => {
        retryTimeouts.push(setTimeout(() => {
          if (!handlersComplete) {
            handlersComplete = attachHandlers()
          }
        }, delay))
      })
    }

    const onIdle = () => {
      if (!handlersComplete) {
        handlersComplete = attachHandlers()
      }
    }

    map.on('idle', onIdle)

    return () => {
      retryTimeouts.forEach(clearTimeout)
      map.off('idle', onIdle)
      detachHandlers()
    }
  }, [map, enabled, interactiveLayerIds, openLayers, onFeatureClick])

  return null
}
